/**
 * iter-235 P0 — useShipmentState hook.
 *
 * Returns the canonical SSOT state + CTA list for a given order or
 * shipment dict. Every frontend surface (Order Details, Orders List,
 * Pack & Ship, Shipping Pipeline overflow menus) consumes this hook
 * INSTEAD of re-deriving `awbIsActive` / `has_shipment` locally.
 *
 * Usage:
 *   const { state, label, color, ctas, can } = useShipmentState(order);
 *   {can('cancel_awb') && <Button onClick={cancel}>Cancel AWB</Button>}
 */
import { useMemo } from 'react';
import { readShipmentState, STATE_BADGE_CLASSES, ACTION_LABELS } from '../lib/shipmentCtaMatrix';

export function useShipmentState(item) {
  return useMemo(() => {
    const { state, label, color, ctas } = readShipmentState(item);
    const ctaSet = new Set(ctas);
    return {
      state,
      label,
      color,
      ctas,
      badgeClassName: STATE_BADGE_CLASSES[color] || STATE_BADGE_CLASSES.gray,
      can: (actionKey) => ctaSet.has(actionKey),
      labelFor: (actionKey) => ACTION_LABELS[actionKey] || actionKey,
    };
  }, [
    item?.shipment_state,
    item?.shipment_state_label,
    item?.shipment_state_color,
    // The CTAs array reference may change across re-fetches even when
    // content is identical — stringify is cheap (small array of strings)
    // and keeps the memo stable across React Query refetches.
    JSON.stringify(item?.shipment_ctas || []),
    // Legacy-derivation inputs in case the BE didn't stamp the SSOT yet.
    item?.status,
    item?.awb_number,
    item?.awb,
    item?.awb_cancelled,
    item?.last_cancelled_awb,
    item?.shipment?.awb,
    item?.shipment?.status,
    item?.shipment?.pickup_status,
    item?.shipment?.pickup_scheduled_at,
  ]);
}

export default useShipmentState;
